import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { CalendlyEmbed } from "@/components/CalendlyEmbed";
import { SEO } from "@/components/SEO";
import { NavLink } from "@/components/NavLink";
import { Target, Clock, Users, TrendingUp, CheckCircle, Zap } from "lucide-react";

const About = () => {

  return (
    <>
      <SEO 
        title="About Leaf Rush | Lawn Care & Landscaping Website Design Experts"
        description="We only build websites for lawn care & landscaping businesses. 5+ years experience creating sites that get you more clients. Learn why niche focus matters."
        keywords="lawn care website design agency, landscaping website experts, websites for lawn care businesses"
        canonical="https://www.leafrushmarketing.com/about/"
        ogUrl="https://www.leafrushmarketing.com/about/"
      />
      <div className="flex flex-col">
      {/* Hero */}
      <section className="py-20 md:py-32">
        <div className="container px-4 md:px-6">
          <div className="mx-auto max-w-4xl text-center space-y-6">
            <h1 className="text-4xl md:text-6xl font-bold tracking-tight">
              We <span className="text-primary">Only</span> Build Websites for Lawn Care & Landscaping
            </h1>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              That's not a limitation—it's our superpower. Our niche-only focus means we know your business, 
              your customers, and what actually drives conversions.
            </p>
          </div>
        </div>
      </section>

      {/* Mission */}
      <section className="py-20 bg-accent/20">
        <div className="container px-4 md:px-6">
          <div className="max-w-4xl mx-auto">
            <div className="grid md:grid-cols-2 gap-12 items-center">
              <div>
                <h2 className="text-3xl md:text-4xl font-bold mb-6">Our Mission</h2>
                <p className="text-lg text-muted-foreground mb-4">
                  We believe lawn care and landscaping businesses deserve better than generic web design agencies 
                  that treat every industry the same.
                </p>
                <p className="text-lg text-muted-foreground mb-4">
                  That's why we built Leaf Rush—to be your dedicated lawn care website design partner that truly understands 
                  the seasonality, competition, and trust factors unique to your industry.
                </p>
                <p className="text-lg text-muted-foreground">
                  Our goal is simple: help you build a website that turns visitors into customers, 
                  so you can focus on what you do best—creating beautiful outdoor spaces.
                </p>
              </div>

              <Card className="border-2">
                <CardContent className="pt-6 space-y-4">
                  <div className="flex items-start gap-4">
                    <Target className="h-8 w-8 text-primary flex-shrink-0" />
                    <div>
                      <h3 className="font-semibold mb-1">Niche-Only Focus</h3>
                      <p className="text-sm text-muted-foreground">
                        100% dedicated to lawn care and landscaping businesses
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <Clock className="h-8 w-8 text-primary flex-shrink-0" />
                    <div>
                      <h3 className="font-semibold mb-1">5+ Years Experience</h3>
                      <p className="text-sm text-muted-foreground">
                        Dozens of websites built, refined, and optimized
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <TrendingUp className="h-8 w-8 text-primary flex-shrink-0" />
                    <div>
                      <h3 className="font-semibold mb-1">95% Retention Rate</h3>
                      <p className="text-sm text-muted-foreground">
                        Our clients stick with us because we deliver results
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20">
        <div className="container px-4 md:px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">Why Choose Leaf Rush for Website Design</h2>
            <p className="text-xl text-muted-foreground">
              What makes us different from other web design agencies
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
            <Card>
              <CardContent className="pt-6">
                <Target className="h-10 w-10 text-primary mb-4" />
                <h3 className="font-semibold text-lg mb-2">Deep Industry Knowledge</h3>
                <p className="text-sm text-muted-foreground">
                  We know the difference between lawn maintenance, aeration, and landscaping design. 
                  We know your busy season. We know what homeowners care about when choosing a lawn care company.
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="pt-6">
                <Zap className="h-10 w-10 text-primary mb-4" />
                <h3 className="font-semibold text-lg mb-2">Fast, Professional Delivery</h3>
                <p className="text-sm text-muted-foreground">
                  About two weeks from kickoff to launch if you deliver content on time. 
                  No bureaucracy, no delays—just fast execution from a small, expert team.
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="pt-6">
                <Users className="h-10 w-10 text-primary mb-4" />
                <h3 className="font-semibold text-lg mb-2">Direct Communication</h3>
                <p className="text-sm text-muted-foreground">
                  You talk directly with the people building your website. No account managers, 
                  no middle layers—just clear, fast communication.
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="pt-6">
                <CheckCircle className="h-10 w-10 text-primary mb-4" />
                <h3 className="font-semibold text-lg mb-2">Proven Results</h3>
                <p className="text-sm text-muted-foreground">
                  We've built dozens of high-converting websites for lawn care companies. 
                  We know what works because we've tested it over and over.
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="pt-6">
                <TrendingUp className="h-10 w-10 text-primary mb-4" />
                <h3 className="font-semibold text-lg mb-2">Seasonal Understanding</h3>
                <p className="text-sm text-muted-foreground">
                  We know your revenue is seasonal. That's why we offer flexible seasonal pricing—
                  cheaper in fall/winter, scaled up when you're busiest.
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="pt-6">
                <Target className="h-10 w-10 text-primary mb-4" />
                <h3 className="font-semibold text-lg mb-2">Growth-Focused</h3>
                <p className="text-sm text-muted-foreground">
                  We don't just build pretty websites. Every element is designed to turn visitors 
                  into leads and leads into paying customers.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Our Process Obsession */}
      <section className="py-20 bg-accent/20">
        <div className="container px-4 md:px-6">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold mb-4">Our Process Obsession</h2>
              <p className="text-xl text-muted-foreground">
                We've refined our process over 5+ years and dozens of projects
              </p>
            </div>

            <div className="space-y-8">
              <Card>
                <CardContent className="pt-6">
                  <div className="flex gap-4">
                    <div className="flex-shrink-0">
                      <div className="w-12 h-12 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-xl font-bold">
                        1
                      </div>
                    </div>
                    <div>
                      <h3 className="font-semibold text-lg mb-2">Deep Discovery</h3>
                      <p className="text-muted-foreground">
                        We don't use templates. Every project starts with understanding your specific services, 
                        target customers, and competitive landscape. We dig deep to position you effectively.
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="pt-6">
                  <div className="flex gap-4">
                    <div className="flex-shrink-0">
                      <div className="w-12 h-12 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-xl font-bold">
                        2
                      </div>
                    </div>
                    <div>
                      <h3 className="font-semibold text-lg mb-2">Conversion-First Design</h3>
                      <p className="text-muted-foreground">
                        Every page layout, every button placement, every word is chosen based on what we know converts 
                        for lawn care businesses. No guessing—just proven strategies.
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="pt-6">
                  <div className="flex gap-4">
                    <div className="flex-shrink-0">
                      <div className="w-12 h-12 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-xl font-bold">
                        3
                      </div>
                    </div>
                    <div>
                      <h3 className="font-semibold text-lg mb-2">Fast Iteration</h3>
                      <p className="text-muted-foreground">
                        We move fast but deliberately. Quick feedback loops mean you see progress regularly 
                        and we can launch in about two weeks if you're responsive.
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="pt-6">
                  <div className="flex gap-4">
                    <div className="flex-shrink-0">
                      <div className="w-12 h-12 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-xl font-bold">
                        4
                      </div>
                    </div>
                    <div>
                      <h3 className="font-semibold text-lg mb-2">Continuous Support</h3>
                      <p className="text-muted-foreground">
                        After launch, we provide training, ongoing guidance, and optional add-on services 
                        (Google Ads, GBP optimization) to scale your lead generation.
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Retention Proof */}
      <section className="py-20">
        <div className="container px-4 md:px-6">
          <div className="max-w-3xl mx-auto text-center space-y-6">
            <h2 className="text-3xl md:text-4xl font-bold">95% Client Retention Rate</h2>
            <p className="text-xl text-muted-foreground">
              Most of our clients come back for add-on services (Google Ads, GBP optimization) 
              after seeing how well their website performs. That's the ultimate proof that our approach works.
            </p>
            <div className="grid md:grid-cols-3 gap-6 pt-8">
              <div className="p-6 bg-accent/20 rounded-lg">
                <p className="text-4xl font-bold text-primary mb-2">50+</p>
                <p className="text-muted-foreground">Websites Built</p>
              </div>
              <div className="p-6 bg-accent/20 rounded-lg">
                <p className="text-4xl font-bold text-primary mb-2">5+ Years</p>
                <p className="text-muted-foreground">In Business</p>
              </div>
              <div className="p-6 bg-accent/20 rounded-lg">
                <p className="text-4xl font-bold text-primary mb-2">100%</p>
                <p className="text-muted-foreground">Niche Focus</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-accent/20">
        <div className="container px-4 md:px-6">
          <div className="max-w-3xl mx-auto text-center space-y-8">
            <h2 className="text-3xl md:text-4xl font-bold">Ready to Work with a Niche Expert?</h2>
            <p className="text-xl text-muted-foreground">
              Let's discuss how a high-converting website can help your lawn care or landscaping business grow
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <NavLink to="/contact#get-started">
                <Button size="lg">Get Started</Button>
              </NavLink>
              <NavLink to="/websites">
                <Button size="lg" variant="outline">View Packages</Button>
              </NavLink>
            </div>
          </div>
        </div>
      </section>

      {/* Calendly Scheduling */}
      <CalendlyEmbed />
      </div>
    </>
  );
};

export default About;
